import { EntityCommonInfoModel, FilteredPagedQueryResult, FilteredPagedModel, CommonMultiDeleteModel } from "../../entities/common";
import { MonthlyRawMaterialModel } from "../../entities/monthlyRawMaterial";
import { apiSlice, TagTypes } from "./apiSlice";
import { getQuerySearchParams } from "../../utils";

export const monthlyRawMaterialApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getMonthlyRawMaterialCommonInfo: builder.query<EntityCommonInfoModel, boolean>({
      query: (isCumulative: boolean) => `/monthlyRawMaterial/${isCumulative}/commonInfo`,
      providesTags: [{ type: TagTypes.MonthlyRawMaterial, id: "LIST" }],
    }),
    getMonthlyRawMaterialFilteredPaged: builder.query<FilteredPagedQueryResult<MonthlyRawMaterialModel>, FilteredPagedModel>({
      query: ({ isCumulative, page = 1, pageSize = 10, searchTerm = "", sortFieldName = "", descending = false, ...rest }: FilteredPagedModel) => {
        let url = `/monthlyRawMaterial/${isCumulative}/pageable?page=${page}&pageSize=${pageSize}&searchTerm=${searchTerm}&sortFieldName=${sortFieldName}&descending=${descending}`;
        const restParams = getQuerySearchParams(rest);
        if (restParams) {
          url += `&${restParams}`;
        }

        return url;
      },
      providesTags: (result) =>
        result
          ? [
              { type: TagTypes.MonthlyRawMaterial, id: "PARTIAL-LIST" },
              ...result.data.map(({ id }: { id: number | string }) => ({
                type: TagTypes.MonthlyRawMaterial,
                id,
              })),
            ]
          : [{ type: TagTypes.MonthlyRawMaterial, id: "PARTIAL-LIST" }],
    }),
    getMonthlyRawMaterial: builder.query<MonthlyRawMaterialModel, number>({
      query: (monthlyRawMaterialId) => `/monthlyRawMaterial/${monthlyRawMaterialId}`,
      providesTags: (result, error, arg) => [{ type: TagTypes.MonthlyRawMaterial, id: arg }],
    }),
    scrapMonthlyRawMaterial: builder.query<MonthlyRawMaterialModel, { isCumulative: boolean; tracingNumber: string }>({
      query: ({ isCumulative, tracingNumber }) => ({
        url: `/monthlyRawMaterial/scrap/${isCumulative}`,
        method: "POST",
        body: { tracingNumber },
      }),
      providesTags: [{ type: TagTypes.MonthlyRawMaterial, id: "Scrap" }],
    }),
    addMonthlyRawMaterial: builder.mutation({
      query: (initialMonthlyRawMaterial) => ({
        url: "/monthlyRawMaterial",
        method: "POST",
        body: initialMonthlyRawMaterial,
      }),
      invalidatesTags: [
        { type: TagTypes.MonthlyRawMaterial, id: "LIST" },
        { type: TagTypes.MonthlyRawMaterial, id: "PARTIAL-LIST" },
      ],
    }),
    editMonthlyRawMaterial: builder.mutation({
      query: (monthlyRawMaterial) => ({
        url: `/monthlyRawMaterial/${monthlyRawMaterial.id}`,
        method: "PUT",
        body: monthlyRawMaterial,
      }),
      invalidatesTags: (result, error, arg) => [{ type: TagTypes.MonthlyRawMaterial, id: arg.id }],
    }),
    deleteMonthlyRawMaterial: builder.mutation({
      query: (monthlyRawMaterialId) => ({
        url: `/monthlyRawMaterial/${monthlyRawMaterialId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              { type: TagTypes.MonthlyRawMaterial, id: arg },
              { type: TagTypes.MonthlyRawMaterial, id: "LIST" },
            ],
    }),
    deleteMultiMonthlyRawMaterials: builder.mutation({
      query: (multiDeleteModel: CommonMultiDeleteModel) => ({
        url: "/monthlyRawMaterial/multiDelete",
        method: "DELETE",
        body: multiDeleteModel,
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              ...arg.ids.map((id: number) => ({
                type: TagTypes.MonthlyRawMaterial,
                id,
              })),
              { type: TagTypes.MonthlyRawMaterial, id: "LIST" },
            ],
    }),
  }),
});

export const {
  useGetMonthlyRawMaterialCommonInfoQuery,
  useGetMonthlyRawMaterialFilteredPagedQuery,
  useGetMonthlyRawMaterialQuery,
  useScrapMonthlyRawMaterialQuery,
  useLazyScrapMonthlyRawMaterialQuery,
  useAddMonthlyRawMaterialMutation,
  useEditMonthlyRawMaterialMutation,
  useDeleteMonthlyRawMaterialMutation,
  useDeleteMultiMonthlyRawMaterialsMutation,
} = monthlyRawMaterialApi;
